/**
 * AnalyticsLive — cohort dashboard wired to /api/analytics/cohort/...
 *
 * Renders M1 (Reading Engagement) + M2 (Question Themes) + M3 (Leaderboard)
 * with a date picker for time-travel via the API's `?date=` param.
 *
 * Routed at: /analytics-live/:instituteId/group/:groupId/book/:bookId
 *
 * Mock equivalent: pages/Analytics.tsx (kept untouched for demo).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useAnalyticsCohort } from '../hooks/useAnalyticsLive';
import type { CohortTheme, LeaderboardEntry } from '../types/analyticsLive';

// ─── Reusable shells ───────────────────────────────────────────────────────

const KPICard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, icon, children, className = '' }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`rounded-2xl border p-6 ${
      isDarkMode
        ? 'bg-navy-800/80 border-white/10 backdrop-blur-sm'
        : 'bg-white border-gray-200 shadow-sm'
    } ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <span className={isDarkMode ? 'text-frost-400' : 'text-frost-600'}>{icon}</span>
        <h3 className={`text-sm font-medium uppercase tracking-wider ${
          isDarkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>{title}</h3>
      </div>
      {children}
    </div>
  );
};

const LiveBadge: React.FC = () => {
  const { isDarkMode } = useTheme();
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${
      isDarkMode
        ? 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30'
        : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
    }`}>
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      Live
    </span>
  );
};

const EmptyHint: React.FC<{
  icon?: React.ReactNode;
  title: string;
  hint?: string;
}> = ({ icon, title, hint }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`flex flex-col items-center text-center py-8 px-4 rounded-xl ${
      isDarkMode ? 'bg-navy-700/20' : 'bg-gray-50'
    }`}>
      {icon && (
        <div className={`mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {icon}
        </div>
      )}
      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {title}
      </p>
      {hint && (
        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          {hint}
        </p>
      )}
    </div>
  );
};

// ─── Question Theme Card (mirrors mock's QuestionThemeCard) ────────────────

const QuestionThemeCard: React.FC<{ theme: CohortTheme }> = ({ theme }) => {
  const { isDarkMode } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const samples = theme.sample_questions || [];

  return (
    <div
      className={`rounded-xl border p-4 transition-all cursor-pointer ${
        isDarkMode
          ? 'bg-navy-700/50 border-white/5 hover:border-frost-500/30'
          : 'bg-gray-50 border-gray-200 hover:border-frost-400'
      }`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {theme.name}
            </h4>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isDarkMode ? 'bg-frost-500/20 text-frost-300' : 'bg-frost-100 text-frost-700'
            }`}>
              {theme.n_students} students
            </span>
          </div>
          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {theme.chapter_chip ? `${theme.chapter_chip} · ` : ''}
            {theme.n_questions} questions
          </p>
        </div>
        {expanded ? (
          <ChevronUpIcon className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        ) : (
          <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        )}
      </div>

      {samples[0] && (
        <div className={`mt-2 text-sm italic pl-3 border-l-2 ${
          isDarkMode ? 'text-gray-300 border-frost-500/40' : 'text-gray-600 border-frost-400'
        }`}>
          "{samples[0]}"
        </div>
      )}

      {expanded && samples.length > 1 && (
        <div className="mt-2 space-y-2">
          {samples.slice(1).map((q, i) => (
            <div key={i} className={`text-sm italic pl-3 border-l-2 ${
              isDarkMode ? 'text-gray-300 border-frost-500/40' : 'text-gray-600 border-frost-400'
            }`}>
              "{q}"
            </div>
          ))}
        </div>
      )}

      {expanded && theme.related_concepts.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {theme.related_concepts.map((c, i) => (
            <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full ${
              isDarkMode ? 'bg-navy-800 text-frost-300 border border-frost-500/20' : 'bg-white text-frost-700 border border-frost-200'
            }`}>
              {c.concept}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────

type SortField = 'pages_read' | 'reading_minutes' | 'questions_asked' | 'last_active_at';

const AnalyticsLive: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { instituteId, groupId, bookId } = useParams<{
    instituteId: string;
    groupId: string;
    bookId: string;
  }>();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const { data, loading, error } = useAnalyticsCohort(groupId, bookId, selectedDate);

  const [showLeastRead, setShowLeastRead] = useState(false);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('pages_read');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const onPickStudent = (userId: string) => {
    navigate(`/analytics-live/${instituteId}/group/${groupId}/book/${bookId}/student/${userId}`);
  };

  const sortedStudents = useMemo<LeaderboardEntry[]>(() => {
    const rows = data?.leaderboard || [];
    const filtered = rows.filter((r) => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        (r.full_name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q)
      );
    });
    return [...filtered].sort((a, b) => {
      const av = (a[sortField] ?? 0) as number | string;
      const bv = (b[sortField] ?? 0) as number | string;
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const an = typeof av === 'number' ? av : (av ? Number(new Date(av as string)) : 0);
      const bn = typeof bv === 'number' ? bv : (bv ? Number(new Date(bv as string)) : 0);
      return sortDir === 'asc' ? an - bn : bn - an;
    });
  }, [data?.leaderboard, search, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ArrowUpIcon className="w-3 h-3 inline ml-1" />
      : <ArrowDownIcon className="w-3 h-3 inline ml-1" />;
  };

  const m1 = data?.reading_engagement;
  const m2 = data?.question_themes;
  const cohortHeader = data?.cohort_header;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-navy-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* ─── Header ────────────────────────────────────────────────────── */}
      <div className={`border-b ${isDarkMode ? 'border-white/10 bg-navy-800/50' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold">
                  {cohortHeader?.group_name || 'Cohort'}
                </h1>
                <LiveBadge />
              </div>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {cohortHeader?.book_title || '—'}
                {cohortHeader?.n_active_students != null && (
                  <> &middot; {cohortHeader.n_active_students} {cohortHeader.n_active_students === 1 ? 'student' : 'students'}</>
                )}
                {data?.selected_date && <> &middot; Report: {data.selected_date}</>}
              </p>
            </div>

            {/* Date picker — styled to match mock's book selector */}
            <div className="relative">
              <select
                className={`appearance-none pl-9 pr-8 py-2 rounded-xl text-sm font-medium cursor-pointer outline-none ${
                  isDarkMode
                    ? 'bg-navy-700 border border-white/10 text-white'
                    : 'bg-gray-100 border border-gray-200 text-gray-900'
                }`}
                value={data?.selected_date || ''}
                onChange={(e) => setSelectedDate(e.target.value || null)}
              >
                {(data?.available_dates || []).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
                {!(data?.available_dates || []).length && <option value="">No data yet</option>}
              </select>
              <CalendarDaysIcon className="w-4 h-4 text-frost-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <ChevronDownIcon className={`w-3 h-3 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
            </div>
          </div>
        </div>
      </div>

      {/* ─── Body ──────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {error && (
          <div className={`rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${
            isDarkMode ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-red-200 bg-red-50 text-red-700'
          }`}>
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {loading && !data && (
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Loading cohort analytics…
          </div>
        )}

        {data && (
          <>
            {/* ─── KPI Cards (M1 + M2) ───────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              {/* M1: Reading Engagement */}
              <KPICard title="Reading Engagement" icon={<BookOpenIcon className="w-5 h-5" />}>
                {m1 ? (
                  <>
                    <div className="flex items-baseline gap-3 mb-1">
                      <span className="text-4xl font-bold tabular-nums">{m1.students_engaged}</span>
                      <span className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        / {m1.students_total} read this day
                      </span>
                    </div>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Avg{' '}
                      <span className="font-semibold text-frost-400">
                        {Math.round(m1.avg_pages_per_engaged || 0)} pages
                      </span>{' '}
                      per engaged student
                      {m1.avg_minutes_per_engaged != null && (
                        <>
                          {' · '}
                          <span className="font-semibold text-frost-400">
                            {Math.round(m1.avg_minutes_per_engaged || 0)} min
                          </span>{' '}
                          avg
                        </>
                      )}
                    </p>

                    {/* Chapter coverage */}
                    {m1.chapters_active.length > 0 ? (
                      <div className="mb-4">
                        <p className={`text-xs font-medium uppercase tracking-wider mb-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}>Chapters Being Read</p>
                        <div className="space-y-1.5">
                          {m1.chapters_active.map((ch, i) => {
                            const chTitle = ch.chapter_name
                              ? `Ch ${ch.chapter_number}: ${ch.chapter_name}`
                              : ch.chapter_number != null
                                ? `Ch ${ch.chapter_number}`
                                : 'Unspecified chapter';
                            return (
                              <div key={i} className={`flex justify-between text-sm px-3 py-1.5 rounded-lg ${
                                isDarkMode ? 'bg-navy-700/30' : 'bg-gray-50'
                              }`}>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{chTitle}</span>
                                <span className={`tabular-nums ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {ch.students_reading} students &middot; avg {ch.avg_pages} pg
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <p className={`text-xs italic mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        No per-chapter breakdown yet — page-time tracking accrues from Apr 28 sessions onward.
                      </p>
                    )}

                    {/* Least engaged */}
                    {m1.least_engaged_students.length > 0 && (
                      <>
                        <button
                          onClick={() => setShowLeastRead(!showLeastRead)}
                          className={`text-xs font-medium flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                            showLeastRead
                              ? isDarkMode ? 'bg-frost-500/20 text-frost-300' : 'bg-frost-100 text-frost-700'
                              : isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Students with least pages read
                          {showLeastRead ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
                        </button>
                        {showLeastRead && (
                          <div className="mt-3 space-y-2">
                            {m1.least_engaged_students.map((s, i) => (
                              <div key={i} className={`flex justify-between text-sm px-3 py-2 rounded-lg ${
                                isDarkMode ? 'bg-navy-700/50' : 'bg-gray-100'
                              }`}>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {(s.name as string) || (s.user_id as string) || 'Unknown'}
                                </span>
                                <div className="flex gap-4">
                                  <span className={`font-medium tabular-nums ${
                                    Number(s.pages || 0) === 0
                                      ? 'text-red-400'
                                      : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                  }`}>
                                    {(s.pages as number) ?? 0} pages
                                  </span>
                                  {s.last_active && (
                                    <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                                      {String(s.last_active)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <EmptyHint
                    icon={<BookOpenIcon className="w-8 h-8" />}
                    title="No reading data for this date yet"
                    hint="The nightly rollup hasn't run for this date. Pick another from the picker."
                  />
                )}
              </KPICard>

              {/* M2: Common Question Themes */}
              <KPICard
                title="Common Themes of Questions"
                icon={<ChatBubbleLeftRightIcon className="w-5 h-5" />}
              >
                {m2 ? (
                  <>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span className="font-semibold text-frost-400">{m2.themes.length} themes</span>
                      {' '}from{' '}
                      <span className="tabular-nums">{m2.n_questions_total}</span>{' '}
                      questions across{' '}
                      <span className="tabular-nums">{m2.n_unique_students}</span>{' '}
                      {m2.n_unique_students === 1 ? 'student' : 'students'}
                    </p>

                    {m2.themes.length > 0 ? (
                      <div className="space-y-3">
                        {m2.themes.map((t) => (
                          <QuestionThemeCard key={t.rank} theme={t} />
                        ))}
                      </div>
                    ) : (
                      <EmptyHint
                        icon={<ChatBubbleLeftRightIcon className="w-8 h-8" />}
                        title="No recurring themes this day"
                        hint="Themes need ≥2 students asking about the same topic. Solo questions don't surface."
                      />
                    )}
                  </>
                ) : (
                  <EmptyHint
                    icon={<ChatBubbleLeftRightIcon className="w-8 h-8" />}
                    title="No question theme data yet"
                    hint="Clustering pipeline runs nightly. If chat traffic is light, themes may not surface."
                  />
                )}
              </KPICard>
            </div>

            {/* ─── Student Leaderboard (M3) ─────────────────────────── */}
            <div className={`rounded-2xl border overflow-hidden ${
              isDarkMode ? 'bg-navy-800/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <UserGroupIcon className={`w-5 h-5 ${isDarkMode ? 'text-frost-400' : 'text-frost-600'}`} />
                  <h3 className={`text-sm font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>All Students</h3>
                  {(data.leaderboard || []).length > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-navy-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {data.leaderboard.length}
                    </span>
                  )}
                </div>
                {data.leaderboard.length > 0 && (
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                    isDarkMode ? 'bg-navy-700 border border-white/10' : 'bg-gray-100 border border-gray-200'
                  }`}>
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search students..."
                      className={`bg-transparent text-sm outline-none w-48 ${
                        isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                )}
              </div>

              {data.leaderboard.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`text-xs uppercase tracking-wider ${
                        isDarkMode ? 'text-gray-500 border-t border-white/5' : 'text-gray-400 border-t border-gray-100'
                      }`}>
                        <th className="text-left px-6 py-3 font-medium">Student</th>
                        <th className="text-right px-6 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort('pages_read')}>
                          Pages <SortIcon field="pages_read" />
                        </th>
                        <th className="text-right px-6 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort('reading_minutes')}>
                          Minutes <SortIcon field="reading_minutes" />
                        </th>
                        <th className="text-right px-6 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort('questions_asked')}>
                          Questions <SortIcon field="questions_asked" />
                        </th>
                        <th className="text-right px-6 py-3 font-medium">Active days</th>
                        <th className="text-right px-6 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort('last_active_at')}>
                          Last active <SortIcon field="last_active_at" />
                        </th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedStudents.map((row) => (
                        <tr
                          key={row.user_id}
                          onClick={() => onPickStudent(row.user_id)}
                          className={`cursor-pointer transition-colors group ${
                            isDarkMode
                              ? 'hover:bg-navy-700/50 border-t border-white/5'
                              : 'hover:bg-gray-50 border-t border-gray-100'
                          }`}
                        >
                          <td className="px-6 py-3">
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {row.full_name || row.email || row.user_id.slice(0, 8)}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <span className={`text-sm tabular-nums ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {row.pages_read}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <span className={`text-sm tabular-nums ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {row.reading_minutes}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <span className={`text-sm tabular-nums ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {row.questions_asked}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <span className={`text-sm tabular-nums ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {row.active_days}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {row.last_active_at
                                ? new Date(row.last_active_at).toLocaleDateString()
                                : '—'}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <ChevronRightIcon className={`w-4 h-4 inline transition-transform group-hover:translate-x-0.5 ${
                              isDarkMode ? 'text-gray-500' : 'text-gray-400'
                            }`} />
                          </td>
                        </tr>
                      ))}
                      {sortedStudents.length === 0 && (
                        <tr>
                          <td colSpan={7} className={`px-6 py-6 text-center text-sm ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            No students match "{search}"
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 pb-6">
                  <EmptyHint
                    icon={<UserGroupIcon className="w-8 h-8" />}
                    title="Leaderboard hasn't been built yet"
                    hint={`The nightly all-time stats job builds this. ${cohortHeader?.n_active_students ?? 0} ${cohortHeader?.n_active_students === 1 ? 'student is' : 'students are'} enrolled in this cohort — their stats appear here after the next pipeline run.`}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsLive;
